// MessageBubble.js (updated)

import React, { useMemo, useState, useRef, useEffect } from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Avatar from "../../../../theme/layout/partials/Avatar";
import Divider from "../../../../theme/partials/Divider";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { isEmpty, sanitizeAssistantOutput, sizeDownHeaders, textToClipboard } from "../../../../helpers/Functions";
import LoadingText from "../../../../theme/partials/LoadingText";
import TextView from "../../../../theme/partials/Typography";
import {  Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";

const MessageBubble = ({
	showMeta = false,
	messageThread = [],
	messagesEndRef,
	isSubmitting,
	partialResponse,
	runStatus = "Thinking",
	getFilesDownload,
	aiSettings,
	...props
}) => {
	const [expandedStates, setExpandedStates] = useState({});
	const [showExpandButtonStates, setShowExpandButtonStates] = useState({});
	const messageRefs = useRef([]);

	const isImage = (fileName) => {
		if (!fileName) return false;
		const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
		const fileExtension = fileName.split('.').pop().toLowerCase();
		return imageExtensions.includes(fileExtension);
	};

	const toggleExpand = (index) => {
		setExpandedStates(prev => ({
			...prev,
			[index]: !prev[index]
		}));
	};

	const latestUserIndex = useMemo(() => {
		for (let i = messageThread.length - 1; i >= 0; i--) {
			if (messageThread[i].role === "user") return i;
		}
		return -1;
	}, [messageThread]);

	// Use useEffect to check for overflow after each render
	useEffect(() => {
		messageRefs.current.forEach((ref, index) => {
			if (ref) {
				const hasOverflow = ref.scrollHeight > ref.clientHeight;
				setShowExpandButtonStates(prev => ({
					...prev,
					[index]: hasOverflow
				}));
			}
		});
	}, [messageThread]); // Re-run when messages change


	// Track which code block was copied (by message index)
	const [copiedCodeIndex, setCopiedCodeIndex] = useState(null);
	const handleCopyCode = (codeString, idx) => {
		navigator.clipboard.writeText(codeString);
		setCopiedCodeIndex(idx);
		setTimeout(() => setCopiedCodeIndex(null), 1200);
	};

	const getRenderers = (idx, code) => {
		// 1. Base renderers (Tables, etc.)
		const renderers = {
			table: ({ children }) => <TableContainer sx={{ my: 2 }}><Table size="small">{children}</Table></TableContainer>,
			thead: ({ children }) => <TableHead>{children}</TableHead>,
			tbody: ({ children }) => <TableBody>{children}</TableBody>,
			tr: ({ children }) => <TableRow>{children}</TableRow>,
			th: ({ children }) => <TableCell sx={{ fontWeight: 'bold', bgcolor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>{children}</TableCell>,
			td: ({ children }) => <TableCell sx={{ borderBottom: '1px solid #eee' }}>{children}</TableCell>
		};

		// 2. Only attach the custom code renderer if the toggle is ON
		if (code) {
			renderers.code = ({ node, inline, className, children, ...props }) => {
				const codeString = String(children).replace(/\n$/, '');

				// FIX: If it is inline code (e.g. `word`), render a simple span/code
				const isInline = inline || !codeString.includes('\n');

				if (isInline) {
					// Render as a simple span (or styled code tag) to keep it in the text flow
					return (
						<code 
							className={className} 
							{...props} 
							style={{ 
								backgroundColor: '#f6f8fa', 
								color: '#24292e',
								padding: '0.2em 0.4em', 
								borderRadius: '6px',
								fontSize: '85%',
								fontFamily: 'monospace'
							}}
						>
							{children}
						</code>
					);
				}
				// If it is a BLOCK (triple backticks), render the Box with Copy Button
				return (
					<Box sx={{ position: 'relative', mb: 1 }}>
						<pre style={{ margin: 0 }}>
							<code className={className} {...props}>
								{children}
							</code>
						</pre>

						<IconButton
							size="small"
							onClick={() => handleCopyCode(codeString, idx)}
							sx={{
								position: 'absolute',
								top: 6,
								right: 6,
								zIndex: 2,
								background: '#fff',
								border: '1px solid #eee',
								p: 0.5,
							}}
							title={copiedCodeIndex === idx ? 'Copied!' : 'Copy code'}
						>
							<ContentCopyIcon
								fontSize="small"
								color={copiedCodeIndex === idx ? 'success' : 'inherit'}
							/>
						</IconButton>
					</Box>
				);
			};
		}

		return renderers;
	};

	return messageThread.map((message, index) => {
		const isLastMessage = index === messageThread.length - 1;
		const isLatestUser = index === latestUserIndex;
		const isExpanded = expandedStates[index];
		const isUserMessage = message.role !== "assistant";
		const mustExpand = showExpandButtonStates[index];
		const isImageMessage = isImage(message?.file_name)

		// Set a reasonable maxHeight for the message content box
		// If expanded or image, allow more height, else restrict
		const containerMaxHeight = isUserMessage
			? (isExpanded || isImageMessage ? '60vh' : '18vh')
			: '60vh';
		const containerOverflowY = 'auto';

		return (
			<Box key={index}>
				<Box
					ref={(el) => {
						if (isLatestUser && messagesEndRef) {
							messagesEndRef.current = el;
						}
					}}
					className={`d-flex ${isUserMessage ? "justify-content-end" : "justify-content-start"
						} align-items-center mb-3`
					}
				>
					<Box
						className={`my-2 ${isUserMessage ? "user-chat-container bubble-chat-container" : "ai-chat-container"
							}`}
					>
						<Box
							ref={el => messageRefs.current[index] = el}
							sx={{
								paddingX: {
									xm: 0,
									md: isUserMessage ? "1rem" : "2vw",
								},
								overflowX: 'auto',
								minHeight: '2.5rem',
								wordWrap: 'break-word',
								overflowY: containerOverflowY,
								maxWidth: '80vw',
							}}
						>
							{
								(message?.role === "content" && message.file_name) ? (
									isImageMessage ? (
										<img
											src={process.env.REACT_APP_S3_BUCKET + message.file_location}
											alt={message.file_name}
											style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px' }}
										/>
									) : (
										<a className="color-primary" href={process.env.REACT_APP_S3_BUCKET + message.file_location} target="_blank" rel="noopener noreferrer">
											{message.file_name}
										</a>
									)
								) : (
									<ReactMarkdown
										key={index + '-' + (aiSettings?.codeView ? 'code' : 'text')}
										remarkPlugins={[remarkGfm]}
										rehypePlugins={[rehypeRaw]}
										components={getRenderers(index, aiSettings?.codeView ?? false)}
									>
										{sanitizeAssistantOutput(message.content)}
									</ReactMarkdown>
								)
							}
						</Box>
						{showMeta && (
							(() => {
								if (message?.role === "assistant") return null;

								const createdAt = message.createdAt || message.created_at;
								const inputTokens = message.token_prompt ?? message.token_prompt;
								const outputTokens = message.token_completion ?? message.token_completion;
								const totalTokens = message.token_total ?? message.token_total ?? (typeof inputTokens === 'number' && typeof outputTokens === 'number' ? inputTokens + outputTokens : undefined);

								return (
									<Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'end', gap: 1 }}>
										<TextView fontcolor="secondary" fontsize="small">
											{new Date(createdAt).toLocaleTimeString()}
										</TextView>
									</Box>
								);
							})()
						)}
						{isUserMessage && (mustExpand || !isEmpty(message?.file_ids)) && (
							<Box sx={{ borderTop: "1px outset black" }} className="d-flex flex-column align-items-center">
								<Box
									className={
										(mustExpand && !isEmpty(message?.file_ids))
											? "w-100 d-flex justify-content-around"
											: "w-100 d-flex justify-content-center"
									}
								>
									{mustExpand && (
										<IconButton
											onClick={() => toggleExpand(index)}
											title={isExpanded ? "Minimize" : "Expand"}
										>
											{isExpanded ?
												<ExpandLessIcon className="font-size-medium color-primary" /> :
												<ExpandMoreIcon className="font-size-medium color-primary" />
											}
										</IconButton>
									)}
									{!isEmpty(message?.file_ids) && (
										<TextView
											className={`
											${mustExpand
													? "ms-2"
													: "w-100 text-center"
												}
											mt-2 clickable-text
										`}
											onClick={(e) => {
												getFilesDownload(JSON.stringify(message?.file_ids))
											}}
										>
											Show Files
										</TextView>
									)}
								</Box>
							</Box>
						)}
					</Box>

					{isUserMessage && (
						<Box className="d-flex flex-column align-items-center py-3 h-100">
							{!props.quickAccess && (
								<Avatar userdetail={props.user_details} customclasses="ms-3" />
							)}
						</Box>
					)}
				</Box>

				{!isUserMessage && (
					<>
						<Divider className="px-3" />
						<Box className="d-flex justify-content-end my-2">
							<IconButton title="Like">
								<ThumbUpAltIcon />
							</IconButton>
							<IconButton title="Dislike">
								<ThumbDownAltIcon />
							</IconButton>
							<IconButton title="Copy">
								<ContentCopyIcon onClick={() => textToClipboard(message.content, "Message Copied.")} />
							</IconButton>
						</Box>
					</>
				)}
				{(isSubmitting && isLastMessage) && (
					<Box className="d-flex justify-content-start my-2 px-5">
						<LoadingText
							title={runStatus}
							color="black"
						/>
					</Box>
				)}
			</Box>
		);
	});
};

export default MessageBubble;